// src/pages/Contact/ContactPage.tsx
// Warm, engaging Contact page — themed cards per inquiry type, rounded-[32px] design system

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import { seoConfig } from '../../config/seo';
import {
    Mail, CheckCircle, Shield, Scale,
    HelpCircle, ArrowRight, Clock, MapPin, LucideIcon,
    Bug
} from 'lucide-react';
import { m } from 'framer-motion';
import { Typography } from '../../components/ui/typography';

interface ContactCard {
    icon: LucideIcon;
    color: string;
    bg: string;
    title: string;
    subtitle: string;
    description: string;
    bullets: string[];
    ctaLabel: string;
    ctaSubject: string;
    bulletLabel: string;
}

const contactCards: ContactCard[] = [
    {
        icon: HelpCircle, color: '#1a73e8', bg: '#e8f0fe',
        title: 'General Support',
        subtitle: 'Tool usage · browser compatibility · feature questions',
        description: 'Questions about how a tool works, compatibility with your browser or device, or what features are available? We are happy to help.',
        bullets: ['How to use a specific tool or feature', 'Browser or device compatibility', 'Questions about file formats or limits', 'Suggestions for new tools or features'],
        ctaLabel: 'Email Support', ctaSubject: 'General Support',
        bulletLabel: 'Common enquiry types',
    },
    {
        icon: Bug, color: '#1e8e3e', bg: '#e6f4ea',
        title: 'Bug Reports',
        subtitle: 'Broken functionality · errors · performance issues',
        description: 'Found something that is not working as expected? We take bug reports seriously — the more detail you include, the faster we can reproduce and fix the issue.',
        bullets: ['Your browser name and version', 'Operating system (Windows, Mac, iOS…)', 'Step-by-step reproduction steps', 'Screenshots or error messages if available'],
        ctaLabel: 'Report a Bug', ctaSubject: 'Bug Report',
        bulletLabel: 'What to include in your report',
    },
    {
        icon: Shield, color: '#9334e6', bg: '#f3e8fd',
        title: 'Privacy & Legal',
        subtitle: 'GDPR · CCPA · DPDPA · grievances · terms',
        description: "For data privacy requests, exercising regulatory rights, or any legal enquiries, please reach out with the subject line shown. Grievance Officer details are below.",
        bullets: ['Data access, correction, or deletion requests', 'Cookie or consent-related queries', 'Privacy Policy or Terms of Service questions', 'Formal grievances (Grievance Officer details below)'],
        ctaLabel: 'Privacy Enquiry', ctaSubject: 'Privacy and Legal Enquiry',
        bulletLabel: 'Common enquiry types',
    },
];

const ContactPage: React.FC = () => {
    const [grievanceOpen, setGrievanceOpen] = useState(false);

    return (
        <>
            <SEO {...seoConfig.contact} />
            <div className="min-h-screen bg-white font-brand text-[#202124]">

                {/* Hero */}
                <header className="max-w-7xl mx-auto px-6 pt-16 pb-20 text-center">
                    <Typography variant="hero-title" as="h1" weight="light" className="mb-6 text-[#121317]">
                        We'd love to <span className="text-[#1a73e8]">hear from you</span>
                    </Typography>
                    <Typography variant="hero-subtitle" weight="normal" className="max-w-xl mx-auto text-[#5f6368] mb-10">
                        Questions, feedback, bug reports, or privacy enquiries — choose the right channel below.
                    </Typography>
                    <div className="flex flex-wrap gap-4 justify-center text-[0.85rem] text-[#5f6368]">
                        {[{ icon: Clock, label: '48h response time' }, { icon: Shield, label: 'GDPR · CCPA · DPDPA compliant' }, { icon: MapPin, label: 'Mumbai, India' }].map(({ icon: Icon, label }) => (
                            <span key={label} className="flex items-center gap-2 px-4 py-2 bg-[#f8f9fa] rounded-full border border-[#dadce0]">
                                <Icon size={14} className="text-[#1a73e8]" />{label}
                            </span>
                        ))}
                    </div>
                </header>

                {/* Contact Cards */}
                <section className="max-w-7xl mx-auto px-6 pb-20 border-t border-[#f1f3f4] pt-16">
                    <div className="flex flex-col gap-6">
                        {contactCards.map((card, i) => {
                            const Icon = card.icon;
                            return (
                                <m.div key={card.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}
                                    className="bg-white border border-[#e8eaed] rounded-[32px] overflow-hidden hover:shadow-md transition-shadow duration-300">
                                    <div className="grid grid-cols-1 lg:grid-cols-2">
                                        <div className="p-8 md:p-12 flex flex-col justify-between">
                                            <div>
                                                <div className="flex items-start gap-4 mb-6">
                                                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ backgroundColor: card.bg, color: card.color }}>
                                                        <Icon size={24} strokeWidth={1.5} />
                                                    </div>
                                                    <div>
                                                        <h2 className="text-2xl font-medium text-[#121317]">{card.title}</h2>
                                                        <p className="text-[0.85rem] mt-1" style={{ color: card.color }}>{card.subtitle}</p>
                                                    </div>
                                                </div>
                                                <p className="text-[0.9375rem] text-[#5f6368] leading-[1.75] mb-8">{card.description}</p>
                                            </div>
                                            <a href={`mailto:contact@filefusion.app?subject=${encodeURIComponent(card.ctaSubject)}`}
                                                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full font-medium text-white no-underline transition-all hover:opacity-90 hover:shadow-md text-[0.9375rem] self-start"
                                                style={{ backgroundColor: card.color }}>
                                                <Mail size={16} />{card.ctaLabel}
                                            </a>
                                        </div>
                                        <div className="p-8 md:p-12 flex flex-col justify-center" style={{ backgroundColor: card.bg + '44' }}>
                                            <h4 className="text-[0.75rem] font-semibold uppercase tracking-widest mb-5" style={{ color: card.color }}>{card.bulletLabel}</h4>
                                            <ul className="space-y-4 list-none p-0 m-0">
                                                {card.bullets.map((b, bi) => (
                                                    <li key={bi} className="flex items-start gap-3">
                                                        <CheckCircle size={16} className="shrink-0 mt-0.5" style={{ color: card.color }} />
                                                        <span className="text-[0.9rem] text-[#3c4043] leading-snug">{b}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </m.div>
                            );
                        })}
                    </div>
                </section>

                {/* Security Disclosure */}
                <section className="max-w-7xl mx-auto px-6 pb-20">
                    <m.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
                        className="bg-[#fef7e0] border border-[#f9e8a0] rounded-[32px] p-8 md:p-12">
                        <div className="flex items-start gap-5 mb-6">
                            <div className="w-12 h-12 bg-[#fce8a0] rounded-2xl flex items-center justify-center shrink-0">
                                <Shield size={22} className="text-[#e37400]" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-medium text-[#121317] mb-1">Security Vulnerability Reporting</h2>
                                <p className="text-[0.875rem] text-[#8a6d00]">Please follow responsible disclosure — do not publish before we've fixed it</p>
                            </div>
                        </div>
                        <p className="text-[0.9375rem] text-[#5f6368] leading-[1.75] mb-6">
                            Email <a href="mailto:contact@filefusion.app?subject=Security Vulnerability Report" className="text-[#1a73e8] hover:underline font-medium">contact@filefusion.app</a> with subject line <strong className="text-[#121317]">"Security Vulnerability Report"</strong>. Please include:
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            {['A clear description of the vulnerability and its potential impact', 'Step-by-step reproduction steps', 'Supporting screenshots, proof-of-concept, or network traces'].map((item, i) => (
                                <div key={i} className="flex items-start gap-3 bg-white rounded-2xl p-4 border border-[#f0d980]">
                                    <CheckCircle size={16} className="text-[#e37400] shrink-0 mt-0.5" />
                                    <span className="text-[0.875rem] text-[#3c4043] leading-snug">{item}</span>
                                </div>
                            ))}
                        </div>
                        <p className="text-[0.875rem] text-[#8a6d00] mt-6">We acknowledge all reports within 48 hours and won't take legal action against researchers following responsible disclosure.</p>
                    </m.div>
                </section>

                {/* Before You Contact */}
                <section className="bg-[#fafafa] border-t border-[#f1f3f4] py-20">
                    <div className="max-w-7xl mx-auto px-6">
                        <div className="bg-white border border-[#e8eaed] rounded-[32px] p-8 md:p-12">
                            <div className="flex items-center gap-4 mb-8">
                                <div className="w-10 h-10 bg-[#e8f0fe] rounded-2xl flex items-center justify-center">
                                    <CheckCircle size={20} className="text-[#1a73e8]" />
                                </div>
                                <h2 className="text-2xl font-medium text-[#121317]">Before you contact us</h2>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {[
                                    { title: 'Check the FAQ first', desc: 'Many common questions about tool usage, privacy, file limits, and browser support are already answered in our help centre.', link: { to: '/faq', label: 'Browse FAQs →' } },
                                    { title: 'Try a different browser', desc: 'If a tool is not working, try Chrome or Firefox, disable extensions, or clear your cache and cookies before reporting.', link: null },
                                    { title: 'Include the details', desc: 'The more specific you are — browser version, OS, exact error message and steps — the faster we can help.', link: null },
                                ].map((tip, i) => (
                                    <div key={i}>
                                        <h3 className="text-[1rem] font-medium text-[#121317] mb-2">{tip.title}</h3>
                                        <p className="text-[0.875rem] text-[#5f6368] leading-relaxed mb-3">{tip.desc}</p>
                                        {tip.link && <Link to={tip.link.to} className="text-[0.875rem] text-[#1a73e8] font-medium hover:underline no-underline">{tip.link.label}</Link>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* Grievance Officer */}
                <section className="max-w-7xl mx-auto px-6 py-20 border-t border-[#f1f3f4]">
                    <button onClick={() => setGrievanceOpen(o => !o)}
                        className="w-full flex items-center justify-between bg-white border border-[#e8eaed] rounded-[32px] p-8 text-left hover:border-[#1a73e8] transition-colors cursor-pointer">
                        <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-[#e8f0fe] rounded-2xl flex items-center justify-center shrink-0">
                                <Scale size={20} className="text-[#1a73e8]" />
                            </div>
                            <div>
                                <h2 className="text-xl font-medium text-[#121317]">Grievance Officer</h2>
                                <p className="text-[0.875rem] text-[#5f6368]">As required under the Digital Personal Data Protection Act, 2023 (DPDPA)</p>
                            </div>
                        </div>
                        <ArrowRight size={20} className={`text-[#5f6368] shrink-0 transition-transform duration-300 ${grievanceOpen ? 'rotate-90' : ''}`} />
                    </button>
                    {grievanceOpen && (
                        <m.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
                            className="mt-4 bg-[#f8f9fa] rounded-[32px] p-8 border border-[#e8eaed]">
                            <p className="text-[0.9375rem] text-[#5f6368] leading-[1.75] mb-8">
                                In accordance with the <strong className="text-[#121317]">Digital Personal Data Protection Act, 2023</strong> and the <strong className="text-[#121317]">Information Technology Act, 2000</strong>, the following Grievance Officer has been appointed.
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                                {[
                                    { label: 'Name', value: 'Rohit — Proprietor & Grievance Officer', isEmail: false },
                                    { label: 'Email', value: 'contact@filefusion.app', isEmail: true },
                                    { label: 'Address', value: 'Mumbai, Maharashtra, India', isEmail: false },
                                ].map(item => (
                                    <div key={item.label} className="bg-white rounded-2xl p-5 border border-[#e8eaed]">
                                        <div className="text-[0.7rem] font-semibold uppercase tracking-wide text-[#80868b] mb-2">{item.label}</div>
                                        {item.isEmail ? (
                                            <a href={`mailto:${item.value}`} className="text-[0.9rem] text-[#1a73e8] hover:underline font-medium">{item.value}</a>
                                        ) : (
                                            <span className="text-[0.9rem] text-[#121317] font-medium">{item.value}</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                            <p className="text-[0.875rem] text-[#5f6368] leading-relaxed">
                                <strong className="text-[#121317]">Response Timeline:</strong> We will acknowledge your grievance within 48 hours and resolve it within 30 days from the date of receipt.
                            </p>
                        </m.div>
                    )}
                </section>

                {/* Related Links */}
                <section className="max-w-7xl mx-auto px-6 pb-20">
                    <div className="bg-white border border-[#e8eaed] rounded-[32px] p-8 md:p-10">
                        <h3 className="text-sm font-semibold text-[#121317] mb-5">Related Information</h3>
                        <div className="flex flex-wrap gap-3">
                            {[{ to: '/privacy', label: 'Privacy Policy' }, { to: '/terms', label: 'Terms of Service' }, { to: '/about', label: 'About FileFusion' }].map(l => (
                                <Link key={l.to} to={l.to} className="inline-flex items-center px-5 py-2.5 bg-white text-[#3c4043] rounded-full border border-[#dadce0] hover:bg-[#e8f0fe] hover:text-[#1a73e8] transition-colors text-sm font-medium no-underline">{l.label}</Link>
                            ))}
                        </div>
                    </div>
                </section>

            </div>
        </>
    );
};

export default ContactPage;
